/**
 * Hook wrapper around the Rules-panel reactive variable (SE-5506).
 *
 * Exposes the current panel state plus imperative open/close helpers so any
 * surface can drive the panel without touching the raw `makeVar`. Mirrors
 * `useReviewQueueDrawerState`.
 * @module features/player-kanban/hooks/useRulesPanelState
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  type RulesPanelState,
  resetRulesPanel,
  rulesPanelVar,
} from "../stores/rulesPanelStore";

/** Public API returned by {@link useRulesPanelState}. */
export interface UseRulesPanelStateResult {
  /** Current panel state. */
  readonly state: RulesPanelState;
  /**
   * Open the panel scoped to one pipeline board.
   * @param kanbanBoardId - The pipeline whose rules to manage.
   */
  readonly open: (kanbanBoardId: string) => void;
  /** Close the panel. */
  readonly close: () => void;
}

/**
 * Subscribe to the Rules-panel reactive variable + open/close helpers.
 * @returns The {@link UseRulesPanelStateResult}.
 */
export function useRulesPanelState(): UseRulesPanelStateResult {
  const state = useReactiveVar(rulesPanelVar);

  const open = useCallback(
    (kanbanBoardId: string) => rulesPanelVar({ open: true, kanbanBoardId }),
    []
  );

  const close = useCallback(() => resetRulesPanel(), []);

  return useMemo(() => ({ state, open, close }), [state, open, close]);
}
